import React from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";

/**
 * Table to display events for a job order or candidate.
 * Shows upcoming and past events based on start date.
 */
const JobOrderEventsTable = ({ events }) => {
  const now = dayjs();

  // Classify events
  const ongoing = [];
  const upcoming = [];
  const past = [];
  events.forEach((event) => {
    const start = dayjs(event.start);
    const end = dayjs(event.end);
    if (now.isAfter(start) && now.isBefore(end)) {
      ongoing.push(event);
    } else if (start.isAfter(now)) {
      upcoming.push(event);
    } else if (end.isBefore(now)) {
      past.push(event);
    }
  });
  // Sort each group by start time ascending
  const sortByStart = (arr) =>
    arr.sort((a, b) => dayjs(a.start) - dayjs(b.start));
  const orderedEvents = [
    ...sortByStart(ongoing),
    ...sortByStart(upcoming),
    ...sortByStart(past),
  ];

  const getStatus = (event) => {
    const start = dayjs(event.start);
    const end = dayjs(event.end);
    if (now.isAfter(start) && now.isBefore(end)) {
      return { label: "Ongoing", color: "warning" };
    } else if (start.isAfter(now)) {
      return { label: "Upcoming", color: "success" };
    } else {
      return { label: "Past", color: "default" };
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Summary</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>End</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Meet Link</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderedEvents.map((event) => {
            const statusObj = getStatus(event);
            const meetLink = event.conference_data?.entryPoints?.find(
              (ep) => ep.entryPointType === "video"
            )?.uri;
            return (
              <TableRow key={event.id}>
                <TableCell>{event.summary}</TableCell>
                <TableCell>{event.description}</TableCell>
                <TableCell>
                  {dayjs(event.start).format("YYYY-MM-DD HH:mm")}
                </TableCell>
                <TableCell>
                  {dayjs(event.end).format("YYYY-MM-DD HH:mm")}
                </TableCell>
                <TableCell>{event.event_type}</TableCell>
                <TableCell>
                  <Chip
                    label={statusObj.label}
                    color={statusObj.color}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {meetLink ? (
                    <a
                      href={meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Google Meet
                    </a>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

JobOrderEventsTable.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      summary: PropTypes.string.isRequired,
      description: PropTypes.string,
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
      event_type: PropTypes.string.isRequired,
      conference_data: PropTypes.object,
    })
  ).isRequired,
};

export default JobOrderEventsTable;
